/**
 * B4CoachPanel.jsx - Panel de Coach IA (Bloque B4)
 * 
 * Componente para solicitar feedback pedagógico del Coach IA.
 * 
 * Funcionalidades:
 * - Solicitar feedback con pregunta personalizada
 * - Ver límites de uso restantes
 * - Visualizar respuesta del coach con formato
 * - Calificar utilidad de la respuesta
 * - Ver historial reciente de sesiones
 * 
 * Sprint 3 - Student Execution System
 */

import React, { useState, useEffect } from 'react';
import { 
  Bot, 
  Send, 
  AlertCircle, 
  CheckCircle2, 
  Star,
  ThumbsUp,
  ThumbsDown,
  Loader2,
  MessageSquare,
  TrendingUp,
  Clock,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import apiClient from '../services/apiClient';

const B4CoachPanel = ({ groupId, weekNumber, submissionId, isEnabled, weekFinalized = false, hasB2Content = true, onStatusChange }) => {
  const [limits, setLimits] = useState(null);
  const [userPrompt, setUserPrompt] = useState('');
  const [isRequesting, setIsRequesting] = useState(false);
  const [lastResponse, setLastResponse] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const [error, setError] = useState(null);

  // Cargar límites al montar
  useEffect(() => {
    if (isEnabled && groupId && weekNumber) {
      loadLimits();
    }
  }, [groupId, weekNumber, isEnabled]);

  // Auto-cargar historial cuando la semana fue declarada completa
  useEffect(() => {
    if (weekFinalized && groupId && weekNumber) {
      loadHistory();
    }
  }, [weekFinalized, groupId, weekNumber]);

  /**
   * Cargar límites de coaching
   */
  const loadLimits = async () => {
    try {
      const response = await apiClient.get(
        `/student-execution/groups/${groupId}/coach-limits`,
        { params: { weekNumber } }
      );
      setLimits(response.data.data);
      onStatusChange?.(response.data.data.used > 0);
    } catch (error) {
      console.error('Error loading coach limits:', error);
    }
  };

  /**
   * Cargar historial de coaching
   */
  const loadHistory = async () => {
    try {
      const response = await apiClient.get(
        `/student-execution/groups/${groupId}/coach-history`,
        { params: { weekNumber, limit: 5 } }
      );
      setHistory(response.data.data || []);
      setShowHistory(true);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  /**
   * Solicitar feedback del coach
   */
  const handleRequestFeedback = async () => {
    if (!userPrompt.trim() || userPrompt.trim().length < 10) {
      setError('La pregunta debe tener al menos 10 caracteres');
      return;
    }

    if (userPrompt.length > 2000) {
      setError('La pregunta no puede exceder 2000 caracteres');
      return;
    }

    setIsRequesting(true);
    setError(null);
    setLastResponse(null);

    try {
      const response = await apiClient.post(
        `/student-execution/groups/${groupId}/week/${weekNumber}/coach-request`,
        { userPrompt: userPrompt.trim() }
      );

      setLastResponse(response.data.data);
      setUserPrompt('');
      
      // Recargar límites
      await loadLimits();

    } catch (error) {
      console.error('Error requesting feedback:', error);
      
      if (error.response?.data?.code === 'LIMIT_EXCEEDED') {
        setError(`Has alcanzado el límite de consultas para esta semana (${limits?.limit || 3})`);
      } else if (error.response?.data?.code === 'B2_REQUIRED') {
        setError('Debes completar la sección de Trabajo antes de solicitar feedback');
      } else if (error.response?.data?.code === 'CONTENT_FLAGGED') {
        const { fieldLabel, message } = error.response.data;
        setError(message || `El campo "${fieldLabel}" contiene lenguaje inapropiado. Corrígelo antes de volver a intentarlo.`);
      } else {
        setError(error.response?.data?.message || 'Error al solicitar feedback del coach');
      }
    } finally {
      setIsRequesting(false);
    }
  };

  /**
   * Ver feedback histórico al hacer click en un item del historial
   */
  const handleViewHistorySession = (session) => {
    const itemId = session.sessionId || session._id;
    if (selectedHistoryItem === itemId) {
      // Toggle: ocultar si ya está seleccionado
      setSelectedHistoryItem(null);
      setLastResponse(null);
      return;
    }
    setSelectedHistoryItem(itemId);
    setLastResponse({
      sessionId: session.sessionId || session._id,
      feedback: session.response,
      rated: !!session.userFeedback?.rating,
      metrics: session.aiMetrics?.tokenUsage
        ? { tokenUsage: session.aiMetrics.tokenUsage, responseTimeMs: null }
        : null,
      isHistoric: true
    });
    // En modo finalizado la lista permanece visible; solo ocultar en modo activo
    if (!weekFinalized) setShowHistory(false);
  };

  /**
   * Calificar respuesta del coach
   */
  const handleRateFeedback = async (sessionId, rating, helpful) => {
    try {
      await apiClient.post(
        `/student-execution/coach-sessions/${sessionId}/feedback`,
        { rating, helpful }
      );

      // Actualizar respuesta local
      if (lastResponse?.sessionId === sessionId) {
        setLastResponse(prev => ({
          ...prev,
          rated: true,
          userRating: rating,
          userHelpful: helpful
        }));
      }

    } catch (error) {
      console.error('Error rating feedback:', error);
    }
  };

  // Si B4 no está habilitado
  if (!isEnabled) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 border-2 border-dashed border-gray-300">
        <div className="flex items-center justify-center gap-3 text-gray-500">
          <Bot className="w-6 h-6" />
          <p className="text-sm font-medium">
            El Coach IA estará disponible después de completar la sección de Trabajo
          </p>
        </div>
      </div>
    );
  }

  // Si la semana ya fue declarada (B5 Finalized) — muestra historial de consultas
  if (weekFinalized) {
    return (
      <div className="space-y-4">
        {/* Banner de cierre */}
        <div className="bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-200 flex items-center gap-3 text-gray-400">
          <Bot className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm font-medium">
            Semana declarada completada. El Coach IA ya no acepta nuevas consultas para esta semana.
          </p>
        </div>

        {/* Cargando historial */}
        {!showHistory && (
          <div className="flex items-center justify-center py-4 gap-2 text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Cargando consultas anteriores...</span>
          </div>
        )}

        {/* Sin consultas realizadas */}
        {showHistory && history.length === 0 && (
          <div className="text-center py-3 text-sm text-gray-400">
            No se realizaron consultas al Coach IA durante esta semana.
          </div>
        )}

        {/* Lista de consultas realizadas */}
        {showHistory && history.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-600" />
              <h4 className="text-sm font-semibold text-blue-800">
                Consultas realizadas esta semana ({history.length})
              </h4>
            </div>
            <div className="p-3 space-y-2">
              {history.map((session, index) => {
                const itemId = session.sessionId || session._id;
                const isSelected = selectedHistoryItem === itemId;
                return (
                  <div key={session._id || index}>
                    <div
                      onClick={() => handleViewHistorySession(session)}
                      className={`flex items-start justify-between gap-3 rounded-lg px-3 py-2.5 cursor-pointer border transition-all
                        ${isSelected
                          ? 'bg-blue-50 border-blue-300 shadow-sm'
                          : 'bg-gray-50 border-transparent hover:bg-gray-100 hover:border-gray-200'}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-800 font-medium truncate">{session.prompt}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {new Date(session.timestamp).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {session.userFeedback?.rating && (
                          <div className="flex items-center gap-1">
                            <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                            <span className="text-xs font-medium text-gray-700">{session.userFeedback.rating}</span>
                          </div>
                        )}
                        <ChevronDown className={`w-3.5 h-3.5 text-blue-400 transition-transform ${isSelected ? 'rotate-180' : ''}`} />
                      </div>
                    </div>

                    {/* Respuesta expandida inline */}
                    {isSelected && lastResponse && (
                      <div className="mt-1 ml-2 bg-white rounded-lg border border-blue-200 overflow-hidden">
                        <div className="bg-blue-600 px-4 py-2">
                          <div className="flex items-center gap-2 text-white">
                            <Bot className="w-4 h-4" />
                            <span className="text-sm font-semibold">Feedback del Coach</span>
                            <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full font-normal">Sesión histórica</span>
                          </div>
                        </div>
                        <div className="p-4 prose prose-sm max-w-none space-y-2">
                          {lastResponse.feedback.split('\n').map((line, i) => {
                            if (!line.trim()) return <div key={i} className="h-1" />;
                            if (/^\*\*(.+)\*\*$/.test(line.trim())) {
                              const text = line.trim().replace(/^\*\*|\*\*$/g, '');
                              return <p key={i} className="font-semibold text-gray-900 text-sm mt-2 mb-1">{text}</p>;
                            }
                            if (/^[•\-]\s/.test(line.trim())) {
                              const text = line.trim().replace(/^[•\-]\s/, '');
                              return (
                                <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                                  <span dangerouslySetInnerHTML={{ __html: text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
                                </div>
                              );
                            }
                            return (
                              <p key={i} className="text-sm text-gray-700 leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }}
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con límites */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-lg">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Coach IA Pedagógico</h3>
              <p className="text-sm text-gray-600">
                Obtén feedback contextual sobre tu trabajo y mejóralo antes de enviarlo.
              </p>
            </div>
          </div>

          {limits && (
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">
                {limits.used}/{limits.limit}
              </div>
              <p className="text-xs text-gray-600">consultas usadas</p>
            </div>
          )}
        </div>
      </div>

      {/* Formulario de solicitud */}
      {(!limits || limits.remaining > 0) && (
        <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
          {/* Aviso cuando B2 no tiene contenido */}
          {!hasB2Content && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <p className="text-sm text-blue-700">
                Completa la sección de Trabajo antes de consultar al coach. El coach evalúa el trabajo que tu equipo registra ahí.
              </p>
            </div>
          )}
          <label className="block">
            <span className="text-sm font-medium text-gray-700 mb-2 block">
              ¿Qué te gustaría consultar al coach?
            </span>
            <textarea
              value={userPrompt}
              onChange={(e) => setUserPrompt(e.target.value)}
              placeholder="Ejemplo: ¿Cómo puedo mejorar mi análisis de viabilidad técnica?"
              rows={4}
              maxLength={2000}
              disabled={isRequesting || !hasB2Content}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-500">
                Mínimo 10 caracteres
              </span>
              <span className={`text-xs ${userPrompt.length > 1900 ? 'text-orange-600' : 'text-gray-500'}`}>
                {userPrompt.length}/2000
              </span>
            </div>
          </label>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex items-center justify-between">
            <button
              onClick={() => { if (!showHistory) loadHistory(); else setShowHistory(false); }}
              className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2 transition-colors"
            >
              {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {showHistory ? 'Ocultar historial' : 'Ver historial'}
            </button>

            <button
              onClick={handleRequestFeedback}
              disabled={isRequesting || !userPrompt.trim() || userPrompt.trim().length < 10 || !hasB2Content}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {isRequesting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Consultando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Solicitar Feedback
                </>
              )}
            </button>
          </div>

          {/* Historial inline — accordion dentro del mismo card */}
          {showHistory && (
            <div className="border-t border-gray-100 pt-4">
              {history.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-3">No hay consultas anteriores esta semana.</p>
              ) : (
                <div className="space-y-2">
                  {history.map((session, index) => {
                    const itemId = session.sessionId || session._id;
                    const isSelected = selectedHistoryItem === itemId;
                    return (
                    <div
                      key={session._id || index}
                      onClick={() => handleViewHistorySession(session)}
                      className={`flex items-start justify-between gap-3 rounded-lg px-3 py-2.5 cursor-pointer border transition-all
                        ${isSelected
                          ? 'bg-blue-50 border-blue-300 shadow-sm'
                          : 'bg-gray-50 border-transparent hover:bg-gray-100 hover:border-gray-200'}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-800 font-medium truncate">{session.prompt}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {new Date(session.timestamp).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {session.userFeedback?.rating && (
                          <div className="flex items-center gap-1">
                            <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                            <span className="text-xs font-medium text-gray-700">{session.userFeedback.rating}</span>
                          </div>
                        )}
                        <ChevronDown className={`w-3.5 h-3.5 text-blue-400 transition-transform ${isSelected ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Límite alcanzado */}
      {limits && limits.remaining === 0 && (
        <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-orange-900">
                Límite de consultas alcanzado
              </p>
              <p className="text-sm text-orange-700 mt-1">
                Has utilizado todas las consultas disponibles ({limits.limit}) para esta semana.
                Revisa el historial para ver el feedback anterior.
              </p>
              <button
                onClick={() => { if (!showHistory) loadHistory(); else setShowHistory(false); }}
                className="mt-3 flex items-center gap-2 text-sm font-medium text-orange-800 hover:text-orange-900 underline underline-offset-2"
              >
                {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showHistory ? 'Ocultar historial' : 'Ver historial de consultas'}
              </button>
              {/* Historial inline en el banner de límite alcanzado */}
              {showHistory && (
                <div className="mt-3 border-t border-orange-200 pt-3">
                  {history.length === 0 ? (
                    <p className="text-sm text-orange-700">No hay consultas registradas esta semana.</p>
                  ) : (
                    <div className="space-y-2">
                      {history.map((session, index) => {
                        const itemId = session.sessionId || session._id;
                        const isSelected = selectedHistoryItem === itemId;
                        return (
                        <div
                          key={session._id || index}
                          onClick={() => handleViewHistorySession(session)}
                          className={`flex items-start justify-between gap-3 rounded-lg px-3 py-2.5 cursor-pointer border transition-all
                            ${isSelected
                              ? 'bg-white border-orange-400 shadow-sm'
                              : 'bg-orange-100 border-transparent hover:bg-orange-200 hover:border-orange-300'}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-orange-900 font-medium truncate">{session.prompt}</p>
                            <p className="text-xs text-orange-700 mt-0.5">
                              {new Date(session.timestamp).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {session.userFeedback?.rating && (
                              <div className="flex items-center gap-1">
                                <Star className="w-3.5 h-3.5 text-yellow-600 fill-yellow-500" />
                                <span className="text-xs font-medium text-orange-900">{session.userFeedback.rating}</span>
                              </div>
                            )}
                            <ChevronDown className={`w-3.5 h-3.5 text-orange-500 transition-transform ${isSelected ? 'rotate-180' : ''}`} />
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Última respuesta */}
      {lastResponse && (
        <div className="bg-white rounded-lg border-2 border-blue-200 overflow-hidden">
          <div className={`px-5 py-3 ${lastResponse.isHistoric ? 'bg-blue-500' : 'bg-blue-600'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <Bot className="w-5 h-5" />
                <span className="font-semibold">Feedback del Coach</span>
                {lastResponse.isHistoric && (
                  <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full font-normal">
                    Sesión histórica
                  </span>
                )}
              </div>
              {lastResponse.metrics?.tokenUsage && (
                <span className="text-xs text-blue-100">
                  {lastResponse.metrics.responseTimeMs ? `${lastResponse.metrics.responseTimeMs}ms • ` : ''}
                  {lastResponse.metrics.tokenUsage.totalTokens} tokens
                </span>
              )}
            </div>
          </div>

          <div className="p-5 space-y-4">
            {/* Respuesta formateada con markdown */}
            <div className="prose prose-sm max-w-none space-y-3">
              {lastResponse.feedback
                .split('\n')
                .map((line, i) => {
                  // Línea vacía → separador
                  if (!line.trim()) return <div key={i} className="h-1" />;

                  // **texto** → heading de sección
                  if (/^\*\*(.+)\*\*$/.test(line.trim())) {
                    const text = line.trim().replace(/^\*\*|\*\*$/g, '');
                    return <p key={i} className="font-semibold text-gray-900 text-sm mt-3 mb-1">{text}</p>;
                  }

                  // Bullet • o - al inicio
                  if (/^[•\-]\s/.test(line.trim())) {
                    const text = line.trim().replace(/^[•\-]\s/, '');
                    // inline **negritas** dentro del bullet
                    return (
                      <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                        <span dangerouslySetInnerHTML={{ __html: text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
                      </div>
                    );
                  }

                  // Línea normal — inline **negritas**
                  return (
                    <p key={i} className="text-sm text-gray-700 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }}
                    />
                  );
                })
              }
            </div>

            {/* Calificación */}
            {!lastResponse.rated && (
              <div className="border-t border-gray-200 pt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">
                  ¿Te resultó útil este feedback?
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleRateFeedback(lastResponse.sessionId, 5, true)}
                    className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                  >
                    <ThumbsUp className="w-4 h-4" />
                    <span className="text-sm font-medium">Muy útil</span>
                  </button>
                  <button
                    onClick={() => handleRateFeedback(lastResponse.sessionId, 3, true)}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span className="text-sm font-medium">Útil</span>
                  </button>
                  <button
                    onClick={() => handleRateFeedback(lastResponse.sessionId, 1, false)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    <ThumbsDown className="w-4 h-4" />
                    <span className="text-sm font-medium">Poco útil</span>
                  </button>
                </div>
              </div>
            )}

            {lastResponse.rated && (
              <div className="border-t border-gray-200 pt-4 flex items-center gap-2 text-green-600">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-sm font-medium">Gracias por tu feedback</span>
              </div>
            )}

            {/* Uso actual */}
            {lastResponse.usage && (
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Consultas utilizadas:</span>
                  <span className="font-semibold text-gray-900">
                    {lastResponse.usage.used}/{lastResponse.usage.limit}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Historial movido a accordion inline dentro del form card y banner de límite */}
    </div>
  );
};

export default B4CoachPanel;